import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Table, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles } from '~/Styles/reactSelectStyles';
import {
  listarContasDigitalizacao,
  buscarMataDigitalizacao,
  buscarNegocioDigitalizacao,
} from '~/Services/Service';
import EntidadeAutocomplete from './EntidadeAutocomplete';

const formatarMoeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatarData = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
};

const TITULO_POR_TIPO = { M: 'Mata', N: 'Negócio' };

const extrairCodigo = (item, tipo) =>
  tipo === 'M' ? item.codigo_mata : item.codigo_negocio;

const extrairData = (item, tipo) =>
  tipo === 'M' ? item.data_mata : item.data_negocio;

const extrairObs = (item, tipo) =>
  tipo === 'M' ? item.obs : item.observacao;

const formatarDescricaoNegocio = (item, dataFormatada) => {
  const cta = item.conta_deb_codigo;
  const moM = item.codigo_ativo != null ? item.codigo_ativo : '';
  const moP = item.codigo_ativo_pagamento != null ? item.codigo_ativo_pagamento : '';
  const obs = item.observacao ? ` -${item.observacao}` : '';
  const ced = item.conta_cre_nome
    ? `${item.conta_cre_codigo} - ${item.conta_cre_nome}`
    : `${item.conta_cre_codigo}`;
  return `Cta:${cta}-MoM:${moM}-MoP:${moP}${obs} Dt:${dataFormatada} Ced.=${ced}`;
};

const ModalMataNegocio = ({ aberto, tipo, codigoGrupoEmpresa, onSelecionar, onCancelar }) => {
  const [conta, setConta] = useState(null);
  const [moeda, setMoeda] = useState(null);
  const [dataDe, setDataDe] = useState('');
  const [dataAte, setDataAte] = useState('');
  const [valorDe, setValorDe] = useState('');
  const [valorAte, setValorAte] = useState('');
  const [contas, setContas] = useState([]);
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscaExecutada, setBuscaExecutada] = useState(false);

  const titulo = TITULO_POR_TIPO[tipo] || 'Registro';

  useEffect(() => {
    if (!aberto || !codigoGrupoEmpresa) {
      setContas([]);
      return;
    }
    setCarregandoContas(true);
    listarContasDigitalizacao(codigoGrupoEmpresa)
      .then((lista) => setContas(lista || []))
      .catch((err) => {
        console.error('Erro ao listar contas:', err);
        toast.error('Não foi possível carregar a lista de contas.');
        setContas([]);
      })
      .finally(() => setCarregandoContas(false));
  }, [aberto, codigoGrupoEmpresa]);

  useEffect(() => {
    if (!aberto) {
      setConta(null);
      setMoeda(null);
      setDataDe('');
      setDataAte('');
      setValorDe('');
      setValorAte('');
      setResultados([]);
      setBuscaExecutada(false);
    }
  }, [aberto]);

  const limpar = () => {
    setConta(null);
    setMoeda(null);
    setDataDe('');
    setDataAte('');
    setValorDe('');
    setValorAte('');
    setResultados([]);
    setBuscaExecutada(false);
  };

  const buscar = async () => {
    if (!codigoGrupoEmpresa) {
      toast.error('Grupo de Empresa não informado.');
      return;
    }
    const algumFiltro = conta || moeda || dataDe || dataAte || valorDe || valorAte;
    if (!algumFiltro) {
      toast.warning('Informe pelo menos um filtro para buscar.');
      return;
    }

    setBuscando(true);
    setBuscaExecutada(true);
    try {
      const filtros = { ge: codigoGrupoEmpresa };
      if (conta) filtros.conta = conta.codigo;
      if (moeda) filtros.ativo = moeda.codigo;
      if (dataDe) filtros.dataDe = dataDe;
      if (dataAte) filtros.dataAte = dataAte;
      if (valorDe !== '' && valorDe != null) filtros.valorDe = valorDe;
      if (valorAte !== '' && valorAte != null) filtros.valorAte = valorAte;

      const buscador = tipo === 'M' ? buscarMataDigitalizacao : buscarNegocioDigitalizacao;
      const lista = await buscador(filtros);
      setResultados(lista || []);
    } catch (err) {
      const mensagem = err && err.message ? err.message : `Erro ao buscar ${titulo.toLowerCase()}s.`;
      toast.error(mensagem);
      setResultados([]);
    } finally {
      setBuscando(false);
    }
  };

  const selecionar = (item) => {
    let descricao;
    if (tipo === 'N') {
      descricao = formatarDescricaoNegocio(item, formatarData(item.data_negocio));
    } else {
      const cre = item.conta_cre_nome
        ? `${item.conta_cre_codigo} - ${item.conta_cre_nome}`
        : `${item.conta_cre_codigo}`;
      const deb = item.conta_deb_nome
        ? `${item.conta_deb_codigo} - ${item.conta_deb_nome}`
        : `${item.conta_deb_codigo}`;
      const obsTexto = extrairObs(item, tipo);
      const obs = obsTexto ? ` - ${obsTexto}` : '';
      descricao = `Cre:${cre} / Deb:${deb}${obs}`;
    }
    onSelecionar({ codigo: extrairCodigo(item, tipo), descricao });
  };

  return (
    <Modal
      show={aberto}
      onHide={onCancelar}
      size="xl"
      centered
      backdrop="static"
      enforceFocus={false}
      restoreFocus={false}
      dialogClassName="modal-mata-negocio-dialog"
    >
      <style>{`
        .modal-mata-negocio-dialog { max-height: 90vh; margin-top: 5vh; margin-bottom: 5vh; }
        .modal-mata-negocio-dialog .modal-content { max-height: 90vh; display: flex; flex-direction: column; }
        .modal-mata-negocio-dialog .modal-body { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
        .modal-mata-negocio-grid { flex: 1 1 auto; min-height: 180px; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; }
        .modal-mata-negocio-grid table { display: table !important; width: 100%; table-layout: auto; }
        .modal-mata-negocio-grid thead { display: table-header-group !important; }
        .modal-mata-negocio-grid tbody { display: table-row-group !important; }
        .modal-mata-negocio-grid tr { display: table-row !important; }
        .modal-mata-negocio-grid th, .modal-mata-negocio-grid td { display: table-cell !important; vertical-align: middle; word-break: break-word; }
        .modal-mata-negocio-grid thead th { position: sticky; top: 0; background-color: #fff; z-index: 1; box-shadow: inset 0 -1px 0 #dee2e6; }
        @media (max-width: 767.98px) {
          .modal-mata-negocio-dialog { max-width: 100%; width: 100%; max-height: 95vh; margin: 0 auto; }
          .modal-mata-negocio-dialog .modal-content { max-height: 95vh; height: 95vh; border-radius: 0; }
          .modal-mata-negocio-grid { min-height: 200px; }
        }
      `}</style>
      <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <Modal.Title style={{color: '#fff'}}>Buscar {titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="small mb-1">Conta</Form.Label>
              <Select
                options={contas.map((c) => ({
                  value: c.codigo,
                  label: `${c.codigo} - ${c.nome || ''}`,
                  item: c,
                }))}
                value={
                  conta
                    ? { value: conta.codigo, label: `${conta.codigo} - ${conta.nome || ''}` }
                    : null
                }
                onChange={(opt) => setConta(opt ? opt.item : null)}
                placeholder={carregandoContas ? 'Carregando...' : 'Selecione...'}
                isClearable
                isLoading={carregandoContas}
                isDisabled={!codigoGrupoEmpresa}
                noOptionsMessage={() =>
                  !codigoGrupoEmpresa
                    ? 'Selecione um Grupo de Empresa primeiro'
                    : 'Nenhuma conta encontrada'
                }
                styles={{
                  ...customStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <EntidadeAutocomplete
              tipo="A"
              label="Moeda"
              placeholder="Digite código ou nome..."
              entidadeSelecionada={moeda}
              onChangeEntidade={setMoeda}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Data de</Form.Label>
              <Form.Control
                type="date"
                value={dataDe}
                onChange={(e) => setDataDe(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Data até</Form.Label>
              <Form.Control
                type="date"
                value={dataAte}
                onChange={(e) => setDataAte(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Valor de</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder="0,00"
                value={valorDe}
                onChange={(e) => setValorDe(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">Valor até</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder="0,00"
                value={valorAte}
                onChange={(e) => setValorAte(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex justify-content-end mb-3" style={{ gap: '8px' }}>
          <Button variant="outline-secondary" onClick={limpar} disabled={buscando}>
            Limpar
          </Button>
          <Button variant="primary" onClick={buscar} disabled={buscando}>
            {buscando ? (
              <>
                <Spinner animation="border" size="sm" /> Buscando...
              </>
            ) : (
              'Buscar'
            )}
          </Button>
        </div>

        <div className="modal-mata-negocio-grid">
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th style={{ width: '110px', whiteSpace: 'nowrap' }}>Data</th>
              <th>Descrição</th>
              <th style={{ width: '130px', whiteSpace: 'nowrap' }}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {!buscaExecutada && (
              <tr>
                <td colSpan={3} className="text-center text-muted">
                  Informe filtros e clique em <strong>Buscar</strong>.
                </td>
              </tr>
            )}
            {buscaExecutada && !buscando && resultados.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted">
                  {`Nenhum${tipo === 'M' ? 'a mata' : ' negócio'} encontrad${tipo === 'M' ? 'a' : 'o'} com os filtros informados.`}
                </td>
              </tr>
            )}
            {resultados.map((item) => {
              const codigo = extrairCodigo(item, tipo);
              const obsTexto = extrairObs(item, tipo);
              const dataFormatada = formatarData(extrairData(item, tipo));
              const descricaoLinha = tipo === 'N'
                ? formatarDescricaoNegocio(item, dataFormatada)
                : `Cre:${item.conta_cre_codigo}-Deb:${item.conta_deb_codigo}${obsTexto ? `-${obsTexto}` : ''}`;
              return (
                <tr
                  key={codigo}
                  style={{ cursor: 'pointer' }}
                  onClick={() => selecionar(item)}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>{dataFormatada}</td>
                  <td style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{descricaoLinha}</td>
                  <td className="text-right" style={{ whiteSpace: 'nowrap' }}>{formatarMoeda(item.valor)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancelar}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalMataNegocio;
